<!-- @format -->

<template>
	<el-dialog
		v-model="visabled"
		:class="[
			'pc-dialog',
			fullScreen ? 'pc-is-fullscreen' : '',
			!showFooter ? 'pc-dialog-hidden-footer' : '',
			!title ? 'pc-dialog-hidden-title' : '',
		]"
		:fullscreen="fullScreen"
		:close-on-click-modal="false"
		draggable
		@close="onClose"
	>
		<template #header="{ titleId, titleClass }">
			<div class="pc-dialog__title">
				<slot name="header" v-bind="{ titleId, titleClass }">
					<span :id="titleId" :class="titleClass">
						{{ title }}
					</span>
				</slot>
				<div class="pc-dialog__extra">
					<slot name="extra"></slot>
					<el-button v-if="canFullScreen" icon="FullScreen" type="primary" link @click="fullScreen = !fullScreen" />
				</div>
			</div>
		</template>
		<slot />

		<template v-if="showFooter" #footer>
			<slot name="footer">
				<div class="dialog-footer">
					<el-button
						v-if="showConfirmButton"
						:type="confirmButtonType"
						:loading="state.confirmLoading"
						size="default"
						@click="onConfirm"
					>
						{{ confirmButtonText }}
					</el-button>
					<el-button :type="cancelButtonType" plain :loading="state.cancelLoading" size="default" @click="onCancel">
						{{ cancelButtonText }}
					</el-button>
				</div>
			</slot>
		</template>
	</el-dialog>
</template>

<script setup lang="ts">
	interface Props {
		modelValue: boolean;
		confirmButtonText?: string; // 确定按钮文案
		cancelButtonText?: string; // 取消按钮文案
		confirmButtonType?: 'primary' | 'success' | 'warning' | 'danger' | 'info' | 'default';
		cancelButtonType?: 'primary' | 'success' | 'warning' | 'danger' | 'info' | 'default';
		title?: string;
		defaultFullScreen?: boolean;
		canFullScreen?: boolean;
		showFooter?: boolean;
		showConfirmButton?: boolean;
	}

	const props = withDefaults(defineProps<Props>(), {
		confirmButtonText: '保存',
		cancelButtonText: '取消',
		confirmButtonType: 'primary',
		cancelButtonType: 'default',
		showFooter: true,
		showConfirmButton: true,
	});

	const emit = defineEmits(['update:modelValue', 'onConfirm', 'onCancel', 'close']);

	const state = ref<{
		cancelLoading: boolean;
		confirmLoading: boolean;
	}>({
		cancelLoading: false,
		confirmLoading: false,
	});

	const fullScreen = ref(props.defaultFullScreen || false);

	const visabled = computed({
		get() {
			return props.modelValue || false;
		},
		set(val) {
			emit('update:modelValue', val);
		},
	});

	const onConfirm = () => {
		state.value.confirmLoading = true;
		emit('onConfirm', () => {
			state.value.confirmLoading = false;
		});
	};

	const onCancel = () => {
		state.value.cancelLoading = true;
		emit('onCancel', () => {
			state.value.cancelLoading = false;
		});
	};

	const onClose = () => {
		state.value = {
			confirmLoading: false,
			cancelLoading: false,
		};
		emit('close');
	};
</script>

<style lang="scss">
	.pc-dialog {
		.el-dialog__header {
			position: relative;
		}

		.pc-dialog__title {
			width: calc(100% - 40px);
			display: flex;
			align-items: center;
			justify-content: space-between;
		}

		.pc-dialog__extra {
			display: flex;
			align-items: center;
			gap: 8px;
		}

		.el-dialog__headerbtn {
			top: 0;
			height: 100%;
			display: flex;
			align-items: center;
			justify-content: center;
		}

		.el-dialog__body {
			height: calc(100% - 125px);
			overflow-y: auto;
		}
	}

	.pc-dialog.pc-dialog-hidden-footer {
		.el-dialog__body {
			height: calc(100% - 60px - 1px);
			overflow-y: auto;
		}
	}

	.pc-dialog-hidden-title {
		.el-dialog__header {
			border-bottom: none !important;
		}
	}

	.pc-dialog.pc-is-fullscreen {
		max-height: 100%;
	}
</style>
